/**
 * Public Terms of Service — linked from the advertising platform API
 * applications and OAuth consent screens.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { PolicyPage, type PolicySection } from './PolicyPage';
import { COMPANY_NAME, PRODUCT_NAME, CONTACT_EMAIL } from './PublicPageLayout';

const SECTIONS: PolicySection[] = [
  {
    id: 'acceptance',
    heading: 'Acceptance',
    body: (
      <p>
        {PRODUCT_NAME} is provided by {COMPANY_NAME}. By accessing the platform you agree to these terms. If
        you are using {PRODUCT_NAME} on behalf of an organization, you confirm that you are authorized to
        accept these terms for that organization.
      </p>
    ),
  },
  {
    id: 'accounts',
    heading: 'Accounts and access',
    body: (
      <p>
        Accounts are created by invitation only. You are responsible for keeping your credentials
        confidential and for activity that occurs under your account. Accounts may not be shared, and access
        is limited to the organizations assigned to your account. Notify us immediately at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you believe an account has been
        compromised.
      </p>
    ),
  },
  {
    id: 'permitted-use',
    heading: 'Permitted use',
    body: (
      <p>
        {PRODUCT_NAME} may be used only to view and analyze advertising performance data for organizations
        you are authorized to access. You may not attempt to access another organization's data, probe or
        interfere with the security of the platform, scrape or bulk-export data outside the features
        provided, or use the platform to violate the terms of any connected advertising platform.
      </p>
    ),
  },
  {
    id: 'connected-accounts',
    heading: 'Connected advertising accounts',
    body: (
      <p>
        Where a client authorizes {COMPANY_NAME} to connect an advertising account, that authorization is
        used only to retrieve reporting data as described in our{' '}
        <Link to="/privacy">Privacy Policy</Link>. Authorization may be revoked at any time through the
        advertising platform or by contacting us; revoking it will stop new data from being retrieved.
      </p>
    ),
  },
  {
    id: 'accuracy',
    heading: 'Data accuracy',
    body: (
      <p>
        Reporting in {PRODUCT_NAME} is derived from third-party advertising platforms. Those platforms
        restate, delay, and re-attribute figures, so numbers shown here may differ from the platform's own
        interface and are provided for analysis rather than as a billing or accounting record of record.
      </p>
    ),
  },
  {
    id: 'availability',
    heading: 'Availability and changes',
    body: (
      <p>
        The platform is provided on an "as is" and "as available" basis. We may add, change, or remove
        features, and we may suspend access for maintenance or to protect the security of the platform.
      </p>
    ),
  },
  {
    id: 'liability',
    heading: 'Limitation of liability',
    body: (
      <p>
        To the maximum extent permitted by law, {COMPANY_NAME} is not liable for indirect, incidental, or
        consequential damages, or for lost profits or lost data, arising from use of {PRODUCT_NAME}. Nothing
        in these terms limits any obligation set out in a signed services agreement between {COMPANY_NAME}{' '}
        and a client; where such an agreement conflicts with these terms, that agreement controls.
      </p>
    ),
  },
  {
    id: 'termination',
    heading: 'Termination',
    body: (
      <p>
        We may suspend or terminate access if these terms are breached or when a client engagement ends. You
        may stop using the platform at any time and request deletion of your account information.
      </p>
    ),
  },
];

export default function TermsPage() {
  return (
    <PolicyPage
      eyebrow="Legal"
      title="Terms of Service"
      intro={`The terms that govern use of ${PRODUCT_NAME}, the advertising reporting platform operated by ${COMPANY_NAME}.`}
      sections={SECTIONS}
    />
  );
}
